import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import api from '../utils/apiClient';
import styled from 'styled-components';

const LeaderboardContainer = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const PageHeader = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fonts.sizes['4xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const CourseBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #4C83FF, #3A63C0);
  color: white;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing[4]} auto;
  box-shadow: 0 4px 15px rgba(76, 131, 255, 0.3);
  letter-spacing: 1px;
`;

const BackButton = styled.button`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLighter};
    transform: translateX(-2px);
  }
`;

const CourseLeaderboard = () => {
  const { courseId } = useParams();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch course details and leaderboard in parallel
        const [courseResponse, leaderboardResponse] = await Promise.all([
          api.course.getCourseDetails(courseId),
          fetch(`/api/user/leaderboard/courses/${courseId}/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
              'Content-Type': 'application/json',
            },
          })
        ]);

        setCourseData(courseResponse);

        if (!leaderboardResponse.ok) {
          if (leaderboardResponse.status === 403) {
            throw new Error('You don\'t have access to this course leaderboard.');
          } else if (leaderboardResponse.status === 404) {
            throw new Error('Course not found.');
          } else {
            throw new Error('Failed to load course leaderboard.');
          }
        }

        const leaderboardData = await leaderboardResponse.json();
        setLeaderboardData(leaderboardData);
      } catch (err) {
        console.error('Error fetching course leaderboard:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchData();
    }
  }, [courseId]);

  const handleBackClick = () => {
    navigate(`/courses/${courseId}`);
  };

  if (loading) {
    return (
      <Layout>
        <LeaderboardContainer>
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 24px'
            }} />
            <p>Loading course leaderboard...</p>
          </div>
        </LeaderboardContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <LeaderboardContainer>
          <div style={{ textAlign: 'center', padding: '64px 0', color: '#dc3545' }}>
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <BackButton onClick={handleBackClick}>
              ← Back to Course
            </BackButton>
          </div>
        </LeaderboardContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <LeaderboardContainer>
        <BackButton onClick={handleBackClick}>
          ← Back to Course
        </BackButton>
        
        <PageHeader>
          <Title>Course Leaderboard</Title>
          {courseData && (
            <CourseBadge>{courseData.title}</CourseBadge>
          )}
          <p style={{ color: '#6c757d', fontSize: '18px', maxWidth: '600px', margin: '0 auto' }}>
            {courseData?.workshop_restricted 
              ? 'Rankings among workshop participants in this course'
              : 'Rankings among all participants in this course'
            }
          </p>
        </PageHeader>

        {leaderboardData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <h3>No participants yet</h3>
            <p>This course doesn't have any enrolled participants or no one has started learning yet.</p>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr 220px',
              padding: '16px 24px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e0e0e0',
              fontWeight: '600',
              fontSize: '14px',
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              <div>Rank</div>
              <div>Participant</div>
              <div>Progress</div>
            </div>

            {leaderboardData.map((user, index) => (
              <div
                key={user.user_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr 220px',
                  padding: '16px 24px',
                  borderBottom: index === leaderboardData.length - 1 ? 'none' : '1px solid #e0e0e0',
                  alignItems: 'center',
                  transition: 'all 0.3s ease',
                  backgroundColor: user.is_current_user ? 'rgba(10, 255, 112, 0.05)' : 'transparent',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = user.is_current_user 
                    ? 'rgba(10, 255, 112, 0.08)' 
                    : '#f8f9fa';
                  e.currentTarget.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = user.is_current_user 
                    ? 'rgba(10, 255, 112, 0.05)' 
                    : 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                {user.is_current_user && (
                  <div style={{
                    content: '',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    background: 'linear-gradient(135deg, #0AFF70, #06C058)'
                  }} />
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: user.rank === 1 ? '#0AFF70' : user.rank === 2 ? '#09B6FF' : user.rank === 3 ? '#4C83FF' : '#333'
                }}>
                  {user.rank === 1 && <span style={{ fontSize: '24px' }}>🏆</span>}
                  {user.rank === 2 && <span style={{ fontSize: '24px' }}>🥈</span>}
                  {user.rank === 3 && <span style={{ fontSize: '24px' }}>🥉</span>}
                  {user.rank > 3 && <span>#{user.rank}</span>}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px',
                    background: `linear-gradient(135deg, hsl(${(user.user_id * 137) % 360}, 80%, 50%), hsl(${((user.user_id * 137) % 360) + 30}, 70%, 40%))`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {user.name}
                      {user.is_current_user && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          You
                        </span>
                      )}
                    </h3>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: '#333'
                    }}>
                      {user.modules_completed || 0}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6c757d',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Modules
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: '#333'
                    }}>
                      {user.avg_score ? `${user.avg_score.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6c757d',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Avg Score
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: user.course_completed ? '#28a745' : '#ffc107'
                    }}>
                      {user.course_completed ? '✓' : '⏳'}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#6c757d',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Status
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </LeaderboardContainer>
    </Layout>
  );
};

export default CourseLeaderboard;
